import React, { useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "react-query";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from "react-router-dom";
import { SettingsRoutes } from "../../../routes";
import CurrentUser from "../../../apiResources/CurrentUser";
import RecruiterUser from "../../../apiResources/RecruiterUser";
import EmployerUser from "../../../apiResources/EmployerUser";
import Recruiter from "../../../apiResources/Recruiter";
import Employer from "../../../apiResources/Employer";
import AccountDetails from "./account/details";
import AccountPreferences from "./account/preferences";
import CompanyOverview from "./company/overview";
import EmailDomains from "./company/domains";
import CompanyLocation from "./company/location";
import CompanySocials from "./company/socials";
import CompanyBanners from "./company/banners";
import CompanyCustomTerms from "./company/custom-terms";
import CompanyMembers from "./company/members";

const settingsQueryClient = new QueryClient();

type User = RecruiterUser | EmployerUser;
type Company = Recruiter | Employer;

const getUserAndCompany = async (): Promise<{
  user: User;
  company: Company;
}> => {
  const current = (await CurrentUser.first()).data;

  if (current.isRecruiter) {
    const user = (
      await RecruiterUser.includes({
        recruiter: {
          sectors: ["sub_sectors"],
        },
      }).find(current.id)
    ).data;

    const company = (await Recruiter.find(user.companyId)).data;

    return { user, company };
  } else if (current.isEmployer) {
    const user = (await EmployerUser.find(current.id)).data;
    const company = (await Employer.find(user.companyId)).data;

    return { user, company };
  }
};

const Settings = () => {
  const [user, setUser] = useState<User>(null);
  const [company, setCompany] = useState<Company>(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const result = await getUserAndCompany();
        setUser(result.user);
        setCompany(result.company);
      } catch (err) {
        Sentry.captureException(err);
      }
    };

    void fetchUser();
  }, []);

  return (
    <QueryClientProvider client={settingsQueryClient}>
      <Router>
        <Switch>
          <Route exact path={SettingsRoutes.Index}>
            <Redirect to={SettingsRoutes.AccountDetails} />
          </Route>
          <Route path={SettingsRoutes.AccountDetails}>
            <AccountDetails user={user} company={company} />
          </Route>
          <Route path={SettingsRoutes.AccountPreferences}>
            <AccountPreferences user={user} company={company} />
          </Route>
          <Route exact path={SettingsRoutes.CompanyIndex}>
            <Redirect to={SettingsRoutes.CompanyOverview} />
          </Route>
          <Route path={SettingsRoutes.CompanyOverview}>
            <CompanyOverview user={user} company={company} />
          </Route>
          {user?.isEmployer && (
            <Route path={SettingsRoutes.EmailDomains}>
              <EmailDomains
                user={user as EmployerUser}
                company={company as Employer}
              />
            </Route>
          )}
          {user?.isEmployer && (
            <Route path={SettingsRoutes.CompanyBanners}>
              <CompanyBanners
                user={user as EmployerUser}
                company={company as Employer}
              />
            </Route>
          )}
          {user?.isEmployer && (company as Employer)?.canUseCustomTerms && (
            <Route path={SettingsRoutes.CompanyCustomTerms}>
              <CompanyCustomTerms
                user={user as EmployerUser}
                company={company as Employer}
              />
            </Route>
          )}
          <Route path={SettingsRoutes.CompanyLocation}>
            <CompanyLocation user={user} company={company} />
          </Route>
          <Route path={SettingsRoutes.CompanySocials}>
            <CompanySocials user={user} company={company} />
          </Route>
          <Route path={SettingsRoutes.CompanyMembers}>
            <CompanyMembers user={user} company={company} />
          </Route>
        </Switch>
      </Router>
    </QueryClientProvider>
  );
};

export default Settings;
